import {PackageList} from './journalTypes';

export type JournalRow = {
  number: string | number;
  creator: string;
  type: string;
  productionDate: Date;
  expiryDate: Date;
  volume: string;
  remainder?: string;
  packageType: string;
  packageQuantity: string;
  addDate: Date;
  comment: string;
  vetDocument: number;
  productItem: string;
  guid?: string;
  uuid?: string;
  unit?: string;
  packageList?: PackageList[];
};

export type AddJournalRowType = Omit<JournalRow, 'productionDate' | 'expiryDate'> & {
  productionDate: string;
  expiryDate: string;
};

export type MercuryPaginationParams = {
  count: number | string;
  offset: number | string;
};

export type BaseMercuryRequestParams = {
  listOptions?: MercuryPaginationParams;
  [key: string]: any;
};

export type OnlyGuidObj = {
  guid: string;
};

export enum TTransactionStatus {
  ACTIVE = 'ACTIVE',
  REJECTED = 'REJECTED',
  PENDING = 'PENDING',
  CLOSED = 'CLOSED',
}

export type TransactionsRow = {
  number: string | number;
  status: TTransactionStatus;
  apiary: string;
  transportType: number;
  transportCountry: string;
  transportNumber: string;
  storageMethod: number;
  recipientEnterprise: string;
  recipientCompany: string;
  tnnNumber?: string | number;
  tnnDate?: Date;
  tnnType?: string | number;
  productionNumber?: number | string;
  productionPurpose?: string;
  productionVolume: string;
  productionWellBeing?: string;
  productionVSE?: string;
  date: Date;
  lastUpdateDate: Date;
};

export type JournalParams = {
  search?: string;
  sortBy?: string;
  sort?: 'ASC' | 'DESC';
};

export type TAddressItem = {
  name: string;
  giud: string;
  uuid: string;
};

export type TEnterprise = {
  name: string;
  type: string;
  numberList: string[];
  address?: {
    country?: {
      name: string;
      guid: string;
      uuid: string;
    };
    region?: {
      name: string;
      guid: string;
      uuid: string;
    };
    locality?: {
      name: string;
      guid: string;
      uuid: string;
    };
    street?: {
      name: string;
      guid: string;
      uuid: string;
    };
    house?: string;
    building?: string;
    postIndex?: string;
    addressView?: string;
  };
  owner: {
    juridicalAddress: {
      addressView: string;
    };
    guid: string;
    uuid: string;
  };
  registryStatus: number;
  guid: string;
  active: boolean;
  last: boolean;
  status: string;
  createDate: string;
  updateDate: string;
  previous?: string;
  next?: string;
  uuid: string;
};

export type MercuryPackageType = {
  guid: string;
  uuid: string;
  name: string;
  code: string;
};

export type TMercuryProductType = {
  name: string;
  code: string;
  productGuid: string;
  guid: string;
  active: boolean;
  last: boolean;
  createDate: string;
  updateDate: string;
  uuid: string;
};

export type TMercuryUnit = {
  name: string;
  fullName: string;
  commonUnitGuid: string;
  factor: string;
  guid: string;
  active: boolean;
  last: boolean;
  status: string;
  createDate: string;
  updateDate: string;
  uuid: string;
};

export type TMercuryPurpose = {
  name: string;
  guid: string;
  active: boolean;
  last: boolean;
  status: string;
  createDate: string;
  updateDate: string;
  uuid: string;
};

export type ProductiveBatchItem = {
  productType: number;
  product: OnlyGuidObj;
  subProduct: OnlyGuidObj;
  productItem?: OnlyGuidObj;
  volume: number;
  unit: OnlyGuidObj;
  dateOfProduction: {
    firstDate: {
      year: number;
      month: number;
      day: number;
      hour: number;
    };
  };
  expiryDate: {
    firstDate: {
      year: number;
      month: number;
      day: number;
      hour: number;
    };
  };
  batchID?: string[];
  perishable: boolean;
  lowGradeCargo: boolean;
  packageList: [
    {
      level: number;
      packingType: OnlyGuidObj;
      quantity: string;
    },
  ];
  origin?: {
    country: OnlyGuidObj;
    producer: {
      enterprise: OnlyGuidObj;
    }[];
  };
};

export type GetProductItemListParams = {
  listOptions: MercuryPaginationParams;
  productType?: number;
  product?: OnlyGuidObj;
  subProduct?: OnlyGuidObj;
  businessEntity?: OnlyGuidObj;
  producer?: {
    businessEntity?: OnlyGuidObj;
  };
};

export type TMercuryProductItem = {
  name: string;
  code: string;
  productType: number;
  product: {
    guid: string;
    uuid: string;
  };
  subProduct: {
    guid: string;
    uuid: string;
  };
  correspondsToGost: boolean;
  guid: string;
  active: boolean;
  last: boolean;
  status: string;
  createDate: string;
  updateDate: string;
  uuid: string;
};

export type AddJournalParams = {
  enterprise: OnlyGuidObj;
  businessEntity?: OnlyGuidObj;
  productionOperation: {
    finalizeOperation?: boolean;
    productiveBatch: ProductiveBatchItem[];
  };
  vetDocument?: [
    {
      authentication: {
        cargoExpertized: number;
      };
    },
  ];
};

export type TMercuryError = {
  code: string;
  value: string;
};

export type TSelectItemNum = {
  key: number;
  value: string;
};

export type TSelectItemStr = {
  key: string;
  value: string;
};

export type TCountry = {
  name: string;
  fullName: string;
  englishName: string;
  code: string;
  code3: string;
  guid: string;
  active: boolean;
  last: boolean;
  status: string;
  createDate: string;
  updateDate: string;
  uuid: string;
};

export type TBusinessEntity = {
  type: number;
  fio: string;
  name: string;
  inn: string;
  ogrn: string;
  juridicalAddress: {
    country: {
      name: string;
      guid: string;
      uuid: string;
    };
    region: {
      name: string;
      guid: string;
      uuid: string;
    };
    locality: {
      name: string;
      guid: string;
      uuid: string;
    };
    subLocality: {
      name: string;
      guid: string;
      uuid: string;
    };
    street: {
      name: string;
      guid: string;
      uuid: string;
    };
    house: string;
    building: string;
    room: string;
    postIndex: string;
    addressView: string;
  };
  guid: string;
  active: boolean;
  last: boolean;
  status: string;
  createDate: string;
  updateDate: string;
  previous: string;
  uuid: string;
};

export interface AddTransactionDelivery {
  deliveryDate: string;
  consignor: {
    businessEntity: OnlyGuidObj;
    enterprise: OnlyGuidObj;
  };
  consignee: {
    businessEntity?: OnlyGuidObj;
    enterprise: OnlyGuidObj;
  };
  consignment: {
    sourceStockEntry: {
      uuid: string;
    };
    id: string;
    volume: number;
    unit: OnlyGuidObj;
    packageList?: PackageList[];
  }[];
  broker?: OnlyGuidObj;
  transportInfo: {
    transportType: number;
    transportNumber: {
      vehicleNumber: string;
    };
  };
  transportStorageType?: number;
  accompanyingForms: {
    waybill?: {
      issueSeries: string;
      issueNumber: string;
      issueDate: Date;
      type: number;
    };
    vetCertificate: {
      authentication: {
        purpose: OnlyGuidObj;
        cargoInspected: boolean;
        cargoExpertized: number;
        locationProsperity: string;
      };
      for: string;
    }[];
  };
}

export interface AddTransactionParams {
  delivery: AddTransactionDelivery[];
}

export type CountryAutoNumber = {
  id: string;
  name: string;
  plates: CountryAutoPlate[];
};

export type CountryAutoPlate = {
  vehicle_type: number;
  format: string;
};

export interface MercuryState {
  journalRows: JournalRow[];
  journalRowsAll: JournalRow[];
  transactionsRows: TransactionsRow[];
  transactionsRowsAll: TransactionsRow[];
  filterJournalParams: JournalParams;
  filterTransactionParams: JournalParams;
  isJournalLoading: boolean;
  isTransactionLoading: boolean;
  setErrors: boolean;

  enterpriseList: TEnterprise[];
  enterprise: TEnterprise | null;
  defaultEnterprise: TEnterprise | null;
  defaultEnterpriseGuid: string;
  packageTypes: MercuryPackageType[];
  productTypes: TMercuryProductType[];
  currentUnit: TMercuryUnit | null;
  purposes: TMercuryPurpose[];
  productItems: TMercuryProductItem[];
  vetDocumentItems: TSelectItemNum[];

  transportTypes: TSelectItemNum[];
  countries: TCountry[];
  storageMethods: TSelectItemNum[];
  businessEntities: TBusinessEntity[];
  businessEntitity: TBusinessEntity | null;
  defaultBusinessEntitity: TBusinessEntity | null;
  waybillTypes: TSelectItemNum[];
  countryAutoNumbers: CountryAutoNumber[];

  getJournalEntries: () => Promise<void>;
  getTransactionEntries: () => Promise<void>;
  addJournalEntrie: (entrie: AddJournalRowType) => Promise<void>;
  editJournalEntrie: (entrie: JournalRow) => Promise<void>;
  addTransactionEntrie: (entrie: AddTransactionParams) => Promise<void>;
  editTransactionEntrie: (entrie: TransactionsRow) => Promise<void>;
  filterJournal: (params: JournalParams) => void;
  filterTransactions: (params: JournalParams) => void;

  updateEnterprise: (entity: TEnterprise | null) => void;
  getEnterpriseList: (query: string) => Promise<void>;
  getProductByTypeList: () => Promise<void>;
  getSubProductByProductList: (guid?: string) => Promise<void>;
  getUnitList: () => Promise<void>;
  getPurposeList: () => Promise<void>;
  getProductItemList: (params: GetProductItemListParams) => Promise<void>;
  getAllCountryList: () => Promise<void>;
  getBusinessEntityList: (query: string) => Promise<void>;
  getDefaultEnterprise: () => Promise<void>;

  getMercuryDictionaries: () => Promise<void>;
  receiveApplicationResult: (applicationId: string) => Promise<any>;
  checkApplicationStatus: (arg: any) => Promise<any>;
  removeDefaultBusinessEntitity: () => void;
}
